<template>
  <div class="t-page">
    <div class="t-wrapper">
      <svg class="t-mark" viewBox="-8 4 68 100" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path
          d="M38.5653 98.715C36.6403 99.3309 34.2931 99.6398 31.522 99.6398C24.9008 99.6398 20.0711 97.9637 17.0311 94.6115C13.9893 91.2594 12.4703 86.0776 12.4703 79.0645V45.6586H0V40.6882L22.5157 14.7957H27.4807V40.3414H48.2643V45.6586H27.4807V79.9892C27.4807 86.0776 30.4827 89.121 36.4869 89.121C38.1034 89.121 39.6424 88.7941 41.1055 88.1385C42.5668 87.4846 44.454 86.3089 46.7633 84.6129L50.2272 89.4677L38.5653 98.715Z"
          fill="currentColor"
        />
      </svg>
      <div class="t-shadow"></div>
    </div>
    <Transition name="wisecrack" mode="out-in">
      <p :key="index" class="wisecrack">{{ wisecracks[index] }}</p>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';

const wisecracks = [
  'Counting your adverbs. There are several.',
  'Checking if your characters have names AND personalities.',
  'Looking for the sentence that goes on a bit too long.',
  'Asking why the flashback was necessary.',
  'Your dialogue is being examined. Clinically.',
  'Finding the paragraph that could have been a sentence.',
  'Noting every time someone smiled sadly.',
  'Locating the exact moment the pacing went sideways.',
  'Reading your prose. Taking deep breaths.',
  'Tallying the filter words.',
  'Sharpening the critique.',
  'Selecting your most quotable line.',
  'Finding something genuinely good to say.',
  'Deciding how much to soften the blow.',
  'The editor is choosing their words carefully.',
  'Composing the part where we are honest with you.',
  'Preparing three things that are actually working.',
  'Writing the verdict.',
  'Almost done. We think you will like most of this.',
  'One last look. Then we tell you everything.',
];

const index = ref(0);
let timer: ReturnType<typeof setInterval> | null = null;

onMounted(() => {
  timer = setInterval(() => {
    index.value = (index.value + 1) % wisecracks.length;
  }, 3000);
});

onUnmounted(() => {
  if (timer) clearInterval(timer);
});
</script>

<style scoped>
.t-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 40px;
}

.t-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.t-mark {
  display: block;
  height: 120px;
  width: auto;
  transform-origin: 50% 85%;
  animation: t-nervous 4s ease-in-out infinite;
  color: var(--color-text-primary, #24292f);
}

.t-shadow {
  width: 60px;
  height: 12px;
  background: radial-gradient(ellipse at center, rgba(0, 0, 0, 0.22) 0%, transparent 70%);
  border-radius: 50%;
  margin-top: 6px;
  animation: t-shadow 4s ease-in-out infinite;
}

.wisecrack {
  font-size: 1.1rem;
  font-weight: 500;
  color: var(--color-text-secondary, #666);
  text-align: center;
  max-width: 360px;
  margin: 0;
  line-height: 1.5;
}

.wisecrack-enter-active,
.wisecrack-leave-active {
  transition:
    opacity 0.35s ease,
    transform 0.35s ease;
}
.wisecrack-enter-from {
  opacity: 0;
  transform: translateY(8px);
}
.wisecrack-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@keyframes t-nervous {
  0% {
    transform: rotate(0deg) translateY(0) scaleX(1) scaleY(1);
  }
  5% {
    transform: rotate(9deg) translateY(0) scaleX(1) scaleY(1);
  }
  10% {
    transform: rotate(-8deg) translateY(0) scaleX(1) scaleY(1);
  }
  15% {
    transform: rotate(7deg) translateY(0) scaleX(1) scaleY(1);
  }
  20% {
    transform: rotate(0deg) translateY(0) scaleX(1) scaleY(1);
  }
  26% {
    transform: rotate(0deg) translateY(3px) scaleX(1.06) scaleY(0.9);
  }
  32% {
    transform: rotate(-3deg) translateY(-12px) scaleX(0.93) scaleY(1.1);
  }
  38% {
    transform: rotate(0deg) translateY(2px) scaleX(1.06) scaleY(0.9);
  }
  42% {
    transform: rotate(0deg) translateY(0) scaleX(1) scaleY(1);
  }
  48% {
    transform: rotate(0deg) translateY(0) scaleX(1) scaleY(1);
  }
  53% {
    transform: rotate(0deg) translateY(5px) scaleX(1.1) scaleY(0.85);
  }
  60% {
    transform: rotate(-5deg) translateY(-22px) scaleX(0.88) scaleY(1.18);
  }
  67% {
    transform: rotate(0deg) translateY(4px) scaleX(1.12) scaleY(0.84);
  }
  72% {
    transform: rotate(0deg) translateY(-8px) scaleX(0.95) scaleY(1.07);
  }
  76% {
    transform: rotate(0deg) translateY(0) scaleX(1) scaleY(1);
  }
  82% {
    transform: rotate(-6deg) translateY(0) scaleX(1) scaleY(1);
  }
  87% {
    transform: rotate(6deg) translateY(0) scaleX(1) scaleY(1);
  }
  92% {
    transform: rotate(-3deg) translateY(0) scaleX(1) scaleY(1);
  }
  100% {
    transform: rotate(0deg) translateY(0) scaleX(1) scaleY(1);
  }
}

@keyframes t-shadow {
  0% {
    transform: scaleX(1);
    opacity: 1;
  }
  20% {
    transform: scaleX(1);
    opacity: 1;
  }
  26% {
    transform: scaleX(1.1);
    opacity: 1;
  }
  32% {
    transform: scaleX(0.55);
    opacity: 0.35;
  }
  38% {
    transform: scaleX(1.2);
    opacity: 1;
  }
  42% {
    transform: scaleX(1);
    opacity: 1;
  }
  48% {
    transform: scaleX(1);
    opacity: 1;
  }
  53% {
    transform: scaleX(1.12);
    opacity: 1;
  }
  60% {
    transform: scaleX(0.3);
    opacity: 0.15;
  }
  67% {
    transform: scaleX(1.3);
    opacity: 1;
  }
  72% {
    transform: scaleX(0.75);
    opacity: 0.7;
  }
  76% {
    transform: scaleX(1);
    opacity: 1;
  }
  100% {
    transform: scaleX(1);
    opacity: 1;
  }
}
</style>
